// Données partagées des pages Cours : liste des contenus, thématiques et progression de lecture
import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { useAuth } from "./auth";
import { buildThemes, isLearningContent } from "./courseMeta";
/** Tous les contenus publiés (liste légère, sans le texte) + thématiques construites. */
export function useCoursesData() {
  const coursesQ = useQuery({
    queryKey: ["courses", "all"],
    queryFn: async () => (await api.get("/courses")).data,
  });
  const themesQ = useQuery({
    queryKey: ["course-themes"],
    queryFn: async () => (await api.get("/course-themes")).data,
  });
  const courses = useMemo(() => (coursesQ.data || []).filter(isLearningContent), [coursesQ.data]);
  const themes = useMemo(() => buildThemes(themesQ.data || [], courses), [themesQ.data, courses]);
  return { courses, themes, isLoading: coursesQ.isLoading || themesQ.isLoading };
}
/**
 * Progression de lecture du membre connecté.
 * readIds : Set des cours lus ; markRead(id) : enregistre un cours comme lu (une seule fois).
 */
export function useCourseProgress() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const loggedIn = !!user;
  const key = ["course-progress", user?.id || null];
  const { data } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get("/courses/progress")).data.read_ids || [],
    enabled: loggedIn,
  });
  const readIds = useMemo(() => new Set(loggedIn ? data || [] : []), [data, loggedIn]);
  const markRead = useCallback(
    async (id) => {
      if (!loggedIn || !id || readIds.has(id)) return false;
      await api.post(`/courses/${id}/read`);
      qc.setQueryData(key, (old = []) => (old.includes(id) ? old : [...old, id]));
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loggedIn, readIds, qc, user?.id]
  );
  return { loggedIn, readIds, markRead };
}
